import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import Pagination from "../components/Pagination";
import { BASE_URL, API } from "../api";
import {
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiX,
  FiClipboard,
  FiTrash2,
} from "react-icons/fi";
import { toasts } from "../components/AppToast";

export default function ManageModifayCancle() {
  // LIST STATES

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedType, setSelectedType] = useState("m");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

  // FIRST MODAL - ONLINE BOOKING

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);

  // SECOND MODAL - MODIFY/CANCEL REQUEST

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);

  // DELETE MODAL

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // FORMAT DATE

  const formatDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
      return date;
    }

    return d.toLocaleDateString("en-GB");
  };

  // FORMAT AMOUNT

  const formatAmount = (amount) => {
    const value = Number(amount);

    if (Number.isNaN(value)) {
      return "0.00";
    }

    return value.toFixed(2);
  };

  // FETCH MODIFY/CANCEL REQUESTS

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
  
      const params = {
        page: currentPage,
        limit: limit,
      };
  
      if (fromDate) {
        params.fromDate = fromDate;
      }
  
      if (toDate) {
        params.toDate = toDate;
      }
  
      if (selectedType && selectedType !== "m") {
        params.type = selectedType;
      }
  
      const response = await API.get(
        "/manage-modifyacancel-requests",
        {
          params,
        }
      );
  
      console.log(
        "Modify/Cancel pagination:",
        response.data
      );
  
      if (response.data?.success) {
        setRequests(
          Array.isArray(response.data.data)
            ? response.data.data
            : []
        );
  
        setTotal(
          Number(response.data.totalRecords || 0)
        );
  
        setTotalPages(
          Number(response.data.totalPages || 1)
        );
      } else {
        setRequests([]);
        setTotal(0);
        setTotalPages(1);
  
        toasts.error(
          response.data?.message ||
            "Unable to fetch requests"
        );
      }
    } catch (error) {
      console.error(
        "Fetch requests error:",
        error
      );
  
      setRequests([]);
      setTotal(0);
      setTotalPages(1);
  
      toasts.error(
        error.response?.data?.message ||
          "Unable to fetch modify/cancel requests"
      );
    } finally {
      setLoading(false);
    }
  }, [
    fromDate,
    toDate,
    selectedType,
    currentPage,
    limit,
  ]);
  // INITIAL LOAD

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // RESET

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setSelectedType("m");
    setCurrentPage(1);
  };

  // SECOND MODAL
  // VIEW MODIFY/CANCEL REQUEST

  const handleViewRequest = async (item) => {
    if (!item?.modify_id) {
      toasts.error("Modify ID not found");
      return;
    }

    try {
      setRequestLoading(true);
      setShowRequestModal(true);
      setViewRequest(null);

      const response = await API.get(
        `/manage-modifyacancel-requests/${item.modify_id}`
      );

      if (response.data?.success) {
        setViewRequest(response.data.data);
      } else {
        toasts.error(response.data?.message || "Unable to load request");

        setShowRequestModal(false);
      }
    } catch (error) {
      console.error("View modify/cancel request error:", error);

      toasts.error(
        error.response?.data?.message || "Unable to load request details"
      );

      setShowRequestModal(false);
    } finally {
      setRequestLoading(false);
    }
  };

  // CLOSE SECOND MODAL

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setViewRequest(null);
  };

  // FIRST MODAL
  // VIEW ONLINE BOOKING

  const handleViewBooking = async (item) => {
    if (!item?.bookingid) {
      toasts.error("Booking ID not found");
      return;
    }

    try {
      setBookingLoading(true);
      setShowBookingModal(true);
      setViewBooking(null);

      const response = await API.get(`/online-booking/view/${item.bookingid}`);

      if (response.data?.success) {
        setViewBooking(response.data.data);
      } else {
        toasts.error(response.data?.message || "Unable to load booking");

        setShowBookingModal(false);
      }
    } catch (error) {
      console.error("View booking error:", error);

      toasts.error(
        error.response?.data?.message || "Unable to load booking details"
      );

      setShowBookingModal(false);
    } finally {
      setBookingLoading(false);
    }
  };

  // CLOSE FIRST MODAL

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setViewBooking(null);
  };

  // =========================================================
  // OPEN DELETE MODAL
  // =========================================================

  const handleOpenDeleteModal = (item) => {
    if (!item) {
      toasts.error("Request not found");
      return;
    }

    if (!item?.modify_id) {
      toasts.error("Modify ID not found");
      return;
    }

    setDeleteRequest(item);
    setShowDeleteModal(true);
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================

  const closeDeleteModal = () => {
    if (deleteLoading) return;

    setShowDeleteModal(false);
    setDeleteRequest(null);
  };

  // =========================================================
  // DELETE REQUEST
  // =========================================================

  const handleDelete = async () => {
    const modifyId = deleteRequest?.modify_id;

    if (!modifyId) {
      toasts.error("Modify ID not found");
      return;
    }

    try {
      setDeleteLoading(true);

      const response = await API.delete(
        `/manage-modifyacancel-requests/${modifyId}`
      );

      if (response.data?.success) {
        toasts.success(
          response.data.message || "Request deleted successfully"
        );

        setShowDeleteModal(false);
        setDeleteRequest(null);

        fetchRequests();
      } else {
        toasts.error(
          response.data?.message || "Unable to delete request"
        );
      }
    } catch (error) {
      console.error("Delete request error:", error);

      toasts.error(
        error.response?.data?.message || "Unable to delete request"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

// =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      currentPage <= 1
    ) {
      return;
    }
  
    setCurrentPage((prev) =>
      Math.max(1, prev - 1)
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      currentPage >= totalPages
    ) {
      return;
    }
  
    setCurrentPage((prev) =>
      Math.min(
        totalPages,
        prev + 1
      )
    );
  };


  // RENDER

  return (
    <Layout activePage="Manage Modify / Cancel Requests">
      <main className="dashboard-main">
        <div className="table-card">
          {/* =====================================================
              PAGE HEADER
          ===================================================== */}

          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage Modify / Cancel Requests</span>

              <span className="rec-count ms-1">
                {total} records
              </span>
            </h3>

            <button
              type="button"
              className="um-btn-secondary btn-sm"
              onClick={fetchRequests}
              disabled={loading}
            >
              <FiRefreshCw
                className={loading ? "me-1 spinner" : "me-1"}
              />{" "}
              Refresh
            </button>
          </div>

          {/* =====================================================
              SEARCH / FILTERS
          ===================================================== */}

          <div className="p-3 border-bottom bg-light bg-opacity-25">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">
                  From
                </label>

                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold small">
                  To
                </label>

                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold small">
                  Type
                </label>

                <select
                  className="form-select season-input"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="m">--Select--</option>

                  <option value="0">Modify</option>

                  <option value="1">Cancel</option>
                </select>
              </div>

              <div className="col-md-3">
                <div className="d-flex gap-2">
                  <button
                    style={{ width: "50%" }}
                    type="button"
                    className="um-btn-secondary flex-grow-1"
                    onClick={handleReset}
                  >
                    <FiRefreshCw className="me-1" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* TABLE */}

          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr className="um-table-head text-center">
                  <th style={{ width: "5%" }}>#</th>

                  <th>Booking Ref. No</th>

                  <th>Email ID</th>

                  <th>Family Name</th>

                  <th>Operation Type</th>

                  <th>Date</th>

                  <th
                    style={{
                      width: "140px",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} className="text-center p-2">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={22} />
                      </div>

                      <div className="empty-title">
                        No records Available
                      </div>
                    </td>
                  </tr>
                ) : (
                  requests.map((item, index) => (
                    <tr
                      key={item.modify_id || index}
                      className="um-row"
                    >
                      <td className="text-center">
                        {(currentPage - 1) * limit + index + 1}
                      </td>

                      {/* CLICK BOOKING REFERENCE */}

                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-danger fw-bold text-decoration-none text-decoration-underline"
                          onClick={() => handleViewRequest(item)}
                          title="View Modify/Cancel Request"
                        >
                          {item.modifyref_no || "-"}
                        </button>
                      </td>

                      <td className="text-center">
                        {item.modify_mail || "-"}
                      </td>

                      <td className="text-center">
                        {item.family_name || "-"}
                      </td>

                      <td className="um-td text-center">
                        <div className="status-wrap justify-content-center m-0">
                          {Number(item.modify_type) === 0 ? (
                            <span className="status-text text-warning fw-bold">
                              Modify
                            </span>
                          ) : Number(item.modify_type) === 1 ? (
                            <span className="status-text text-danger fw-bold">
                              Cancel
                            </span>
                          ) : (
                            "-"
                          )}
                        </div>
                      </td>

                      <td className="text-center">
                        {formatDate(item.modify_createddate)}
                      </td>

                      {/* ACTIONS */}

                      <td className="text-center">
                        <div className="action-buttons d-flex justify-content-center gap-1">
                          {/* VIEW ONLINE BOOKING */}

                          <button
                            type="button"
                            className="action-btn action-view btn btn-sm p-1"
                            title="View Online Booking"
                            onClick={() => handleViewBooking(item)}
                          >
                            <FiEye />
                          </button>

                          {/* DELETE */}

                          <button
                            type="button"
                            className="action-btn action-delete btn btn-sm p-1"
                            title="Delete"
                            onClick={() => handleOpenDeleteModal(item)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}

          <Pagination
            page={currentPage}
            totalPages={totalPages}
            total={total}
            limit={limit}
            loading={loading}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        </div>
      </main>

      {/* =========================================================
          MODIFY / CANCEL REQUEST MODAL
      ========================================================= */}

      {showRequestModal && (
        <div
          className="um-overlay"
          onClick={closeRequestModal}
        >
          <div
            className="um-modal"
            style={{ maxWidth: "700px" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="um-modal-head">
              <h2 className="um-modal-title">
                View Modify / Cancel Request
              </h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeRequestModal}
              >
                <FiX />
              </button>
            </div>

            {/* BODY */}

            <div className="um-modal-scroll">
              {requestLoading ? (
                <div className="text-center py-5">
                  <div className="spinner" />

                  <div className="mt-3 text-muted">
                    Loading request details...
                  </div>
                </div>
              ) : !viewRequest ? (
                <div className="alert alert-warning m-3">
                  No request details found.
                </div>
              ) : (
                <div className="p-3">
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle mb-0">
                      <tbody>
                        <tr>
                          <th
                            style={{
                              width: "35%",
                            }}
                          >
                            Operation type
                          </th>

                          <td>
                            {Number(viewRequest.modify_type) === 0 ? (
                              <span className="badge bg-warning text-dark">
                                Modify
                              </span>
                            ) : Number(
                                viewRequest.modify_type
                              ) === 1 ? (
                              <span className="badge bg-danger">
                                Cancel
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th>Booking reference number</th>

                          <td>
                            <span className="text-danger fw-bold">
                              {viewRequest.modifyref_no || "-"}
                            </span>
                          </td>
                        </tr>

                        <tr>
                          <th>Family name</th>

                          <td>
                            {viewRequest.family_name || "-"}
                          </td>
                        </tr>

                        <tr>
                          <th>Email ID</th>

                          <td>
                            {viewRequest.modify_mail || "-"}
                          </td>
                        </tr>

                        <tr>
                          <th>Modify/Cancellation details</th>

                          <td>
                            <div
                              className="p-2 bg-light border rounded"
                              style={{
                                minHeight: "80px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {viewRequest.modify_comment || "-"}
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <th>Date</th>

                          <td>
                            {formatDate(
                              viewRequest.modify_createddate
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER */}

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeRequestModal}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          FIRST MODAL
          ONLINE BOOKING DETAILS
      ========================================================= */}

      {showBookingModal && (
        <div
          className="um-overlay"
          onClick={closeBookingModal}
        >
          <div
            className="um-modal um-view-modal modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="um-modal-head">
              <h2 className="um-modal-title">
                View Online Booking
              </h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeBookingModal}
              >
                <FiX />
              </button>
            </div>

            {/* LOADING */}

            <div className="um-modal-scroll">
              {bookingLoading ? (
                <div className="text-center py-5">
                  <div className="spinner" />

                  <div className="mt-3 text-muted">
                    Loading booking details...
                  </div>
                </div>
              ) : !viewBooking ? (
                <div className="alert alert-warning m-3">
                  No booking details found.
                </div>
              ) : (
                (() => {
                  const booking =
                    viewBooking?.booking || {};

                  const personal =
                    viewBooking?.personalDetails || {};

                  const itinerary =
                    viewBooking?.itinerary || {};

                  const car =
                    viewBooking?.car || {};

                  const calculations =
                    viewBooking?.calculations || {};

                  const payment =
                    viewBooking?.payment || {};

                  const meta =
                    viewBooking?.meta || {};

                  const regularClient =
                    viewBooking?.regularClient || null;

                  const specialDiscount =
                    viewBooking?.specialDiscount || null;

                  const freeExtras =
                    Array.isArray(viewBooking?.freeExtras)
                      ? viewBooking.freeExtras
                      : [];

                  const paidExtras =
                    Array.isArray(viewBooking?.paidExtras)
                      ? viewBooking.paidExtras
                      : [];

                  const freeInsurance =
                    Array.isArray(
                      viewBooking?.freeInsurance
                    )
                      ? viewBooking.freeInsurance
                      : [];

                  const paidInsurance =
                    Array.isArray(
                      viewBooking?.paidInsurance
                    )
                      ? viewBooking.paidInsurance
                      : [];

                  return (
                    <>
                      {/* BOOKING SUMMARY */}

                      <div className="profile-header mb-4">
                        <div className="row g-0 w-100">
                          <div className="col-md-4 p-3 border-end">
                            <div className="form-label fw-semibold">
                              Booking Ref. No.
                            </div>

                            <div className="profile-row-value text-danger fw-bold">
                              {meta.bookingReference ||
                                booking.fullbooking_no ||
                                meta.bookingId ||
                                "—"}
                            </div>
                          </div>

                          <div className="col-md-4 p-3 border-end">
                            <div className="form-label fw-semibold">
                              Booking Date
                            </div>

                            <div className="profile-row-value">
                              {formatDate(
                                meta.bookingDate ||
                                  booking.online_bookingdate
                              )}
                            </div>
                          </div>

                          <div className="col-md-4 p-3">
                            <div className="form-label fw-semibold">
                              Payment Status
                            </div>

                            <div
                              className={`profile-row-value ${
                                Number(
                                  payment.paymentStatus
                                ) === 1
                                  ? "text-success fw-bold"
                                  : "text-danger fw-bold"
                              }`}
                            >
                              {Number(
                                payment.paymentStatus
                              ) === 1
                                ? "Paid"
                                : "Unpaid"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* PERSONAL INFORMATION */}

                      <div className="sec-div">
                        <span>
                          PERSONAL INFORMATION
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Name
                              </div>

                              <div className="profile-row-value">
                                {personal.title || ""}{" "}
                                {personal.firstName || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Family Name
                              </div>

                              <div className="profile-row-value">
                                {personal.lastName || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Email ID
                              </div>

                              <div className="profile-row-value">
                                {personal.email || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Telephone
                              </div>

                              <div className="profile-row-value">
                                {personal.phone || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Mobile Number
                              </div>

                              <div className="profile-row-value">
                                {personal.mobile || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Gender
                              </div>

                              <div className="profile-row-value">
                                {personal.gender || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Country
                              </div>

                              <div className="profile-row-value">
                                {personal.country || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Address
                              </div>

                              <div className="profile-row-value">
                                {personal.address || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Zip Code
                              </div>

                              <div className="profile-row-value">
                                {personal.postcode || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                City
                              </div>

                              <div className="profile-row-value">
                                {personal.city || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Pick Location Details
                              </div>

                              <div className="profile-row-value">
                                {itinerary.pickupDetails ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Drop Location Details
                              </div>

                              <div className="profile-row-value">
                                {itinerary.dropDetails ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ITINERARY */}

                      <div className="sec-div mt-4">
                        <span>
                          ITINERARY DETAILS
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Pick Up Location
                              </div>

                              <div className="profile-row-value">
                                {itinerary.pickupLocation ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Pick Up Date
                              </div>

                              <div className="profile-row-value">
                                {formatDate(
                                  itinerary.pickupDate
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Pick Up Time
                              </div>

                              <div className="profile-row-value">
                                {itinerary.pickupTime ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Return Location
                              </div>

                              <div className="profile-row-value">
                                {itinerary.returnLocation ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Return Date
                              </div>

                              <div className="profile-row-value">
                                {formatDate(
                                  itinerary.returnDate
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Return Time
                              </div>

                              <div className="profile-row-value">
                                {itinerary.returnTime ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Total Duration
                              </div>

                              <div className="profile-row-value">
                                {itinerary.durationDays ??
                                  0}{" "}
                                Days
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Duration Hours
                              </div>

                              <div className="profile-row-value">
                                {itinerary.durationHours ??
                                  0}{" "}
                                Hours
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Driver Age
                              </div>

                              <div className="profile-row-value">
                                {personal.driverAge ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CAR DETAILS */}

                      <div className="sec-div mt-4">
                        <span>CAR DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Car Model Name
                              </div>

                              <div className="profile-row-value">
                                {car.name || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Passenger
                              </div>

                              <div className="profile-row-value">
                                {car.passenger ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Luggage
                              </div>

                              <div className="profile-row-value">
                                {car.luggage ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-1">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Doors
                              </div>

                              <div className="profile-row-value">
                                {car.doors ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-1">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                A.C.
                              </div>

                              <div className="profile-row-value">
                                {car.airCondition ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Engine Capacity
                              </div>

                              <div className="profile-row-value">
                                {car.engineCapacity || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Car Image
                              </div>

                              <div className="profile-row-value">
                                {car.image ? (
                                  <img
                                    src={`${BASE_URL}/${car.image}`}
                                    alt={
                                      car.image_alt ||
                                      car.image
                                    }
                                    style={{
                                      width: "50%",
                                      borderRadius: "6px",
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display =
                                        "none";
                                    }}
                                  />
                                ) : (
                                  "—"
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* EXTRA DETAILS */}

                      {(freeExtras.length > 0 ||
                        paidExtras.length > 0 ||
                        freeInsurance.length > 0 ||
                        paidInsurance.length > 0) && (
                        <>
                          <div className="sec-div mt-4">
                            <span>EXTRA DETAILS</span>
                          </div>

                          {freeExtras.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Free Optional Extras
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                      <th>Quantity</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {freeExtras.map(
                                      (item, index) => (
                                        <tr
                                          key={
                                            item.id ||
                                            index
                                          }
                                        >
                                          <td className="text-danger">
                                            {item.name || "—"}
                                          </td>

                                          <td className="text-danger">
                                            {item.quantity ?? 0}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {paidExtras.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Paid Optional Extras
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                      <th>Quantity</th>
                                      <th>Rate/Day</th>
                                      <th>Days</th>
                                      <th>Max Rate</th>
                                      <th>Total Amount</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {paidExtras.map(
                                      (item, index) => (
                                        <tr
                                          key={
                                            item.optdet_id ||
                                            item.id ||
                                            index
                                          }
                                        >
                                          <td className="text-danger">
                                            {item.optitem_name ||
                                              item.name ||
                                              "—"}
                                          </td>

                                          <td className="text-danger text-center">
                                            {item.quantity ??
                                              item.online_extraitemqty ??
                                              0}
                                          </td>

                                          <td className="text-danger">
                                            €{" "}
                                            {formatAmount(
                                              item.rate ??
                                                item.opt_dayrate ??
                                                0
                                            )}
                                          </td>

                                          <td className="text-danger text-center">
                                            {item.days ??
                                              itinerary.durationDays ??
                                              0}
                                          </td>

                                          <td className="text-danger">
                                            €{" "}
                                            {formatAmount(
                                              item.maxRate ??
                                                item.opt_maxrate ??
                                                0
                                            )}
                                          </td>

                                          <td className="text-danger fw-semibold">
                                            €{" "}
                                            {formatAmount(
                                              item.totalAmount ??
                                                0
                                            )}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {freeInsurance.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Free Insurance Details
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {freeInsurance.map(
                                      (item, index) => (
                                        <tr
                                          key={
                                            item.id ||
                                            index
                                          }
                                        >
                                          <td className="text-danger">
                                            {item.name || "—"}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {paidInsurance.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Paid Insurance Details
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                      <th>Rate/Day</th>
                                      <th>Days</th>
                                      <th>Total Amount</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {paidInsurance.map(
                                      (item, index) => (
                                        <tr
                                          key={
                                            item.insdtl_id ||
                                            item.id ||
                                            index
                                          }
                                        >
                                          <td className="text-danger">
                                            {item.insitem_name ||
                                              item.name ||
                                              "—"}
                                          </td>

                                          <td className="text-danger">
                                            €{" "}
                                            {formatAmount(
                                              item.rate ??
                                                item.online_itemrate ??
                                                0
                                            )}
                                          </td>

                                          <td className="text-danger text-center">
                                            {item.days ??
                                              itinerary.durationDays ??
                                              0}
                                          </td>

                                          <td className="text-danger fw-semibold">
                                            €{" "}
                                            {formatAmount(
                                              item.totalAmount ??
                                                0
                                            )}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* REGULAR CLIENT */}

                      {regularClient && (
                        <>
                          <div className="sec-div mt-4">
                            <span>
                              REGULAR CLIENT DETAILS
                            </span>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Client Id
                                  </div>

                                  <div className="profile-row-value">
                                    {regularClient.clientId ||
                                      "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Client Email
                                  </div>

                                  <div className="profile-row-value">
                                    {regularClient.clientEmail ||
                                      "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Discount on per day price
                                  </div>

                                  <div className="profile-row-value text-danger">
                                    {regularClient.discountPercent ??
                                      0}{" "}
                                    %
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Discount Amount
                                  </div>

                                  <div className="profile-row-value text-danger">
                                    €{" "}
                                    {formatAmount(
                                      regularClient.discountAmount
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* SPECIAL DISCOUNT */}

                      {specialDiscount && (
                        <>
                          <div className="sec-div mt-4">
                            <span>
                              SPECIAL DISCOUNT DETAILS
                            </span>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Per day special discount amount
                                  </div>

                                  <div className="profile-row-value text-danger">
                                    €{" "}
                                    {formatAmount(
                                      specialDiscount.discountAmount
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* DISCOUNT */}

                      <div className="sec-div mt-4">
                        <span>DISCOUNT</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Discount Type
                              </div>

                              <div className="profile-row-value">
                                {Number(
                                  booking.online_disctype
                                ) === 1
                                  ? "Regular Client"
                                  : Number(
                                      booking.online_disctype
                                    ) === 2
                                  ? "Special Discount"
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Discount Amount
                              </div>

                              <div className="profile-row-value text-danger">
                                €{" "}
                                {formatAmount(
                                  booking.online_discamount
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* RATE CHART */}

                      <div className="sec-div mt-4">
                        <span>
                          RATE CHART DETAILS
                        </span>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-bordered table-striped align-middle">
                          <thead className="table-light">
                            <tr className="um-table-head">
                              <th>Quote</th>
                              <th>Calculation</th>
                              <th>Total Amount</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td className="text-danger">
                                Rental Price
                              </td>

                              <td className="text-danger">
                                €{" "}
                                {formatAmount(
                                  calculations.rentalPrice
                                )}{" "}
                                ×{" "}
                                {calculations.rentalDays ??
                                  0}{" "}
                                Days
                              </td>

                              <td className="text-danger">
                                €{" "}
                                {formatAmount(
                                  calculations.rentalTotal
                                )}
                              </td>
                            </tr>

                            {Number(
                              calculations.extraHourCharge
                            ) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Extra Hour Delay Charges
                                  <br />
                                  (For{" "}
                                  {calculations.extraHours ??
                                    0}{" "}
                                  Hours)
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.extraHourCharge
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(
                              calculations.oneWayRentalCharges
                            ) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  One Way Rental Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.oneWayRentalCharges
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(
                              calculations.pickupCharge
                            ) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Pick Up Location Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.pickupCharge
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(
                              calculations.dropoffCharge
                            ) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Drop Off Location Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.dropoffCharge
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(
                              calculations.totalLocationPrice
                            ) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Total Location Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.totalLocationPrice
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(
                              calculations.totalInsurance
                            ) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Total Insurance
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.totalInsurance
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* TOTAL */}

                      <div className="row g-0 border mt-3">
                        <div className="col-md-6 p-3 fw-semibold">
                          Total Price
                        </div>

                        <div className="col-md-6 p-3 text-end text-danger fs-5 fw-bold">
                          €{" "}
                          {formatAmount(
                            calculations.totalPrice
                          )}
                        </div>
                      </div>

                      {/* PAYMENT */}

                      <div className="sec-div mt-4">
                        <span>PAYMENT DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Advance Paid
                              </div>

                              <div className="profile-row-value text-danger">
                                {payment.advancePercent ??
                                  0}{" "}
                                % of total price
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Final Total Rental Price
                              </div>

                              <div className="profile-row-value text-danger fw-bold">
                                €{" "}
                                {formatAmount(
                                  calculations.totalPrice
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Advance Paid Amount
                              </div>

                              <div className="profile-row-value text-danger fw-bold">
                                − €{" "}
                                {formatAmount(
                                  payment.advanceAmount
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Balance to Pay
                              </div>

                              <div className="profile-row-value text-danger fw-bold">
                                €{" "}
                                {formatAmount(
                                  payment.balanceToPay
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Payment Status
                              </div>

                              <div className="profile-row-value">
                                {Number(
                                  payment.paymentStatus
                                ) === 1
                                  ? "Paid"
                                  : "Unpaid"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()
              )}
            </div>

            {/* FOOTER */}

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeBookingModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          DELETE REQUEST MODAL
      ========================================================= */}

      {showDeleteModal && (
        <div
          className="um-overlay"
          onClick={
            deleteLoading
              ? undefined
              : closeDeleteModal
          }
        >
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">

              {/* DELETE ICON */}

              <div className="delete-icon">
                <FiTrash2 size={32} />
              </div>

              {/* DELETE TITLE */}

              <h2 className="delete-title">
                Delete Modify / Cancel Request?
              </h2>

              {/* DELETE DESCRIPTION */}

              <p className="delete-description">
                Are you sure you want to delete{" "}

                <span className="badge badge-success">
                  "
                  {deleteRequest?.modifyref_no ||
                    deleteRequest?.modify_ref_no ||
                    deleteRequest?.bookingref_no ||
                    deleteRequest?.booking_ref_no ||
                    "this request"}
                  "
                </span>

                ?

                <br />

                This will permanently remove
                this modify/cancel request.

                <br />

                <strong>
                  This action cannot be undone.
                </strong>
              </p>

              {/* DELETE ACTIONS */}

              <div className="delete-actions">

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={deleteLoading}
                  className="delete-confirm-btn"
                  onClick={handleDelete}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>

              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}